import React, { useEffect, useRef, useState } from 'react'
import Typography from '@mui/material/Typography'
import {
  selectLabraChatLayout,
  setLabraChatLayout,
  useLabraChat,
  useLabraChatContext,
  useLabraChatDispatch,
  useLabraChatSelector,
} from '@labrav/common'
import { ButtonV2, LoadingSpinner } from '@labrav/react-components'
import {
  PocCalculatorEdit,
  PocCalculatorPreviewResponse,
  previewPocCalculator,
} from '../../api/fundingApi'
import { CalculatorResourceRows } from '../CalculatorResourceRows/CalculatorResourceRows'
import { useStyles } from './PocCalculatorReview.styles'

interface PocCalculatorReviewProps {
  opportunityId: string
  templateId: string
}

export const PocCalculatorReview: React.FC<PocCalculatorReviewProps> = ({
  opportunityId,
  templateId,
}) => {
  const { classes } = useStyles()
  const { accountId, chatEndpoint } = useLabraChatContext()
  const { sendPrompt, isStreaming, messages } = useLabraChat()
  const layout = useLabraChatSelector(selectLabraChatLayout)
  const dispatch = useLabraChatDispatch()
  const lastMessage = messages[messages.length - 1]
  const isLatestTurn =
    lastMessage?.role === 'ASSISTANT' &&
    Boolean(
      lastMessage.openUiLang?.includes(opportunityId) &&
        lastMessage.openUiLang?.includes(templateId)
    )
  const activeOnMountRef = useRef(isLatestTurn)
  const [preview, setPreview] = useState<PocCalculatorPreviewResponse | null>(
    null
  )
  const [edits, setEdits] = useState<PocCalculatorEdit[]>([])
  const [loading, setLoading] = useState(activeOnMountRef.current)
  const [error, setError] = useState('')
  const [decision, setDecision] = useState<'accept' | 'reject' | null>(null)
  const expandOnLoadRef = useRef(isStreaming)

  const serverError = (err: unknown) => {
    const serverMessage = (
      err as { response?: { data?: { errors?: unknown[] } } }
    )?.response?.data?.errors?.[0]
    return typeof serverMessage === 'string' && serverMessage
      ? serverMessage
      : // TODO(funding): placeholder copy until product reviews it.
        'Unable to load the pricing calculator estimate.'
  }

  useEffect(() => {
    if (!activeOnMountRef.current) return
    let cancelled = false
    setLoading(true)
    setError('')
    previewPocCalculator(chatEndpoint, accountId, {
      opportunity_id: opportunityId,
      template_id: templateId,
      edits: [],
    })
      .then(({ data }) => {
        if (cancelled) return
        setPreview(data)
        setEdits([])
        if (
          expandOnLoadRef.current &&
          (layout === null || layout === 'floating')
        ) {
          expandOnLoadRef.current = false
          dispatch(setLabraChatLayout('expanded'))
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(serverError(err))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [chatEndpoint, accountId, opportunityId, templateId])

  const resources = (preview?.resources ?? []).map(resource => {
    const planned = preview?.plan.resources.find(
      row => row.resource_id === resource.resource_id
    )
    const edit = edits.find(row => row.resource_id === resource.resource_id)
    return {
      ...resource,
      upfront_cost_usd: planned?.new_upfront_usd ?? resource.upfront_cost_usd,
      monthly_cost_usd: edit
        ? Number(edit.monthly_usd)
        : (planned?.new_monthly_usd ?? resource.monthly_cost_usd),
      fields: resource.fields.map(field => ({
        ...field,
        current_value:
          edit?.values[field.field_id] ??
          planned?.fields[field.field_id]?.new ??
          field.current_value,
      })),
    }
  })
  const projectedArr =
    12 * resources.reduce((sum, resource) => sum + resource.monthly_cost_usd, 0)

  const locked = Boolean(decision) || !isLatestTurn

  if (!activeOnMountRef.current) return null

  return (
    <div className={classes.root} data-testid="poc-calculator-review">
      {preview ? (
        <CalculatorResourceRows
          resources={resources}
          onEdit={
            locked
              ? undefined
              : edit =>
                  setEdits(prev => [
                    ...prev.filter(row => row.resource_id !== edit.resource_id),
                    edit,
                  ])
          }
        />
      ) : null}
      {loading ? (
        <div className={classes.loading} data-testid="poc-calculator-loading">
          <LoadingSpinner />
        </div>
      ) : null}
      {error ? (
        <Typography component="span" className={classes.error} role="alert">
          {error}
        </Typography>
      ) : null}
      <div className={classes.footer}>
        {preview ? (
          <div className={classes.arr} data-testid="poc-calculator-arr">
            {/* TODO(funding): placeholder label until product reviews it. */}
            <Typography component="span" className={classes.arrLabel}>
              Projected ARR
            </Typography>
            <Typography component="span" className={classes.arrValue}>
              {`$${projectedArr.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`}
            </Typography>
          </div>
        ) : null}
        {/* TODO(funding): placeholder button and chat copy until product reviews it. */}
        <ButtonV2
          styleType="outlined"
          smallVariant
          disabled={!preview || loading || locked}
          onClick={() => {
            setDecision('reject')
            sendPrompt(
              `Do not start the POC with this pricing calculator estimate.\n\`\`\`json\n${JSON.stringify(
                {
                  action: 'poc_calculator_reject',
                  opportunity_id: opportunityId,
                  template_id: templateId,
                }
              )}\n\`\`\``,
              { displayMessage: "Don't start the POC yet." }
            )
          }}
        >
          Not now
        </ButtonV2>
        <ButtonV2
          styleType="primary"
          smallVariant
          disabled={!preview || loading || locked}
          onClick={async () => {
            if (!preview) return
            setDecision('accept')
            let previewToken = preview.preview_token
            if (edits.length) {
              setLoading(true)
              setError('')
              try {
                const { data } = await previewPocCalculator(
                  chatEndpoint,
                  accountId,
                  {
                    opportunity_id: opportunityId,
                    template_id: templateId,
                    edits,
                  }
                )
                previewToken = data.preview_token
              } catch (err: unknown) {
                setError(serverError(err))
                setDecision(null)
                return
              } finally {
                setLoading(false)
              }
            }
            sendPrompt(
              `Start the POC with the reviewed pricing calculator estimate.\n\`\`\`json\n${JSON.stringify(
                {
                  action: 'poc_calculator_accept',
                  opportunity_id: opportunityId,
                  template_id: templateId,
                  calculator_preview_token: previewToken,
                  calculator_edits: edits,
                }
              )}\n\`\`\``,
              { displayMessage: 'Start the POC with this estimate.' }
            )
          }}
        >
          Start POC
        </ButtonV2>
      </div>
    </div>
  )
}
